<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
// NOTICE: KAPTION-INTEGRATION - Kaption Platform Style dropdown for the bypass-label picker
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';

export default {
  components: {
    SettingsSection,
    NextButton,
    KaptionFilterSelect,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isEnterprise } = useConfig();
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

    return { v$: useVuelidate(), isEnterprise, isKaptionFlagEnabled };
  },
  data() {
    return {
      selectedAgents: [],
      isAgentListUpdating: false,
      enableAutoAssignment: false,
      maxAssignmentLimit: null,
      kaptionAutoAssignOnHumanReply: true,
      cooldownHours: 0,
      cooldownEndsAtOohBoundary: false,
      autoUnassignOnResolve: 'disabled',
      // NOTICE: KAPTION-INTEGRATION - Keep the human owner when a conversation reopens
      keepHumanOwnerOnReopen: false,
      // NOTICE: KAPTION-INTEGRATION - ai-takeover.CUSTOMER_MESSAGE.1 (off by default)
      takeoverCustomerMessageEnabled: false,
      takeoverCustomerMessage: '',
      takeoverCustomerMessageSaving: false,
      // NOTICE: KAPTION-INTEGRATION - Route a resolved human reopen to assistant triage
      assistantTriagesResolvedHumanReopen: false,
      assistantTriagesResolvedHumanReopenSaving: false,
      manualAssignmentRemoteEnabled: false,
      manualAssignmentRemoteSaving: false,
      manualAssignmentRemoteSavedValue: false,
      // NOTICE: KAPTION-INTEGRATION - Labels that make the AI assistant skip a conversation
      assistantBypassLabels: [],
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      // NOTICE: KAPTION-INTEGRATION
      accountLabels: 'labels/getLabels',
    }),
    // NOTICE: KAPTION-INTEGRATION - { value, label } options for the bypass picker
    accountLabelOptions() {
      return (this.accountLabels || []).map(label => ({
        value: label.title,
        label: label.title,
      }));
    },
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.CUSTOMER_MESSAGE.1
    showTakeoverSettings() {
      return this.isKaptionFlagEnabled('KAPTION_AI_TAKEOVER');
    },
    showCooldownSettings() {
      return this.isKaptionFlagEnabled('KAPTION_POST_RESOLUTION_COOLDOWN');
    },
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return this.$t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
    // NOTICE: KAPTION-INTEGRATION - Ensure account labels are available for the
    // assistant-bypass multiselect
    if (!this.accountLabels.length) {
      this.$store.dispatch('labels/get');
    }
  },
  methods: {
    setDefaults() {
      this.enableAutoAssignment = this.inbox.enable_auto_assignment;
      this.maxAssignmentLimit =
        this.inbox?.auto_assignment_config?.max_assignment_limit || null;
      // NOTICE: KAPTION-INTEGRATION - Default to true if not set
      this.kaptionAutoAssignOnHumanReply =
        this.inbox?.auto_assignment_config
          ?.kaption_auto_assign_on_human_reply !== false;
      // NOTICE: KAPTION-INTEGRATION - Post-resolution cooldown
      this.cooldownHours =
        this.inbox?.auto_assignment_config
          ?.kaption_post_resolution_cooldown_hours || 0;
      // NOTICE: KAPTION-INTEGRATION - Cooldown ends at OOH boundary (opt-in)
      this.cooldownEndsAtOohBoundary =
        this.inbox?.auto_assignment_config
          ?.kaption_cooldown_ends_at_ooo_boundary === true;
      // NOTICE: KAPTION-INTEGRATION - Auto-unassign on resolve
      this.autoUnassignOnResolve =
        this.inbox?.auto_assignment_config?.kaption_auto_unassign_on_resolve ||
        'disabled';
      // NOTICE: KAPTION-INTEGRATION - Keep the human owner when a conversation reopens
      this.keepHumanOwnerOnReopen =
        this.inbox?.auto_assignment_config
          ?.kaption_keep_human_owner_on_reopen === true;
      // NOTICE: KAPTION-INTEGRATION - ai-takeover.CUSTOMER_MESSAGE.1
      this.takeoverCustomerMessageEnabled =
        this.inbox?.auto_assignment_config
          ?.kaption_takeover_customer_message_enabled === true;
      this.takeoverCustomerMessage =
        this.inbox?.auto_assignment_config?.kaption_takeover_customer_message ||
        '';
      // NOTICE: KAPTION-INTEGRATION - Default off; enables the explicit assistant-triage reopen policy
      this.assistantTriagesResolvedHumanReopen =
        this.inbox?.auto_assignment_config
          ?.kaption_assistant_triages_resolved_human_reopen === true;
      const assignmentConfig = this.inbox?.auto_assignment_config || {};
      const hasRemoteSetting = Object.prototype.hasOwnProperty.call(
        assignmentConfig,
        'kaption_manual_assignment_remote_enabled'
      );
      // remote-assignment-enforcement.UI.2 — an explicit remote value wins over legacy safeguards.
      this.manualAssignmentRemoteEnabled = hasRemoteSetting
        ? assignmentConfig.kaption_manual_assignment_remote_enabled === true
        : assignmentConfig.kaption_manual_assignment_capacity_enabled ===
            true ||
          assignmentConfig.kaption_manual_assignment_availability_enabled ===
            true;
      this.manualAssignmentRemoteSavedValue =
        this.manualAssignmentRemoteEnabled;
      // NOTICE: KAPTION-INTEGRATION - Assistant bypass labels
      this.assistantBypassLabels = [
        ...(this.inbox?.auto_assignment_config
          ?.kaption_assistant_bypass_labels || []),
      ];
      this.fetchAttachedAgents();
    },
    async fetchAttachedAgents() {
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.selectedAgents = inboxMembers;
      } catch (error) {
        //  Handle error
      }
    },
    handleEnableAutoAssignment() {
      this.updateInbox();
    },
    async updateAgents() {
      const agentList = this.selectedAgents.map(el => el.id);
      this.isAgentListUpdating = true;
      try {
        await this.$store.dispatch('inboxMembers/create', {
          inboxId: this.inbox.id,
          agentList,
        });
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isAgentListUpdating = false;
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          enable_auto_assignment: this.enableAutoAssignment,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            max_assignment_limit: this.maxAssignmentLimit,
            // NOTICE: KAPTION-INTEGRATION
            kaption_auto_assign_on_human_reply:
              this.kaptionAutoAssignOnHumanReply,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.CUSTOMER_MESSAGE.1
    async saveTakeoverCustomerMessage() {
      if (this.takeoverCustomerMessageSaving) return;
      this.takeoverCustomerMessageSaving = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_takeover_customer_message_enabled:
              this.takeoverCustomerMessageEnabled,
            kaption_takeover_customer_message:
              this.takeoverCustomerMessage.trim(),
          },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.takeoverCustomerMessageSaving = false;
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    handleKaptionAutoAssignChange() {
      this.updateInbox();
    },
    // NOTICE: KAPTION-INTEGRATION - Auto-unassign on resolve
    async updateAutoUnassign(mode) {
      this.autoUnassignOnResolve = mode;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_auto_unassign_on_resolve: mode,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Post-resolution cooldown
    async updateCooldown(hours) {
      this.cooldownHours = hours;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_post_resolution_cooldown_hours: hours,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Cooldown ends at OOH boundary (opt-in)
    async updateCooldownBoundaryFlag(value) {
      this.cooldownEndsAtOohBoundary = value;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_cooldown_ends_at_ooo_boundary: value,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_BOUNDARY_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Keep the human owner when a conversation reopens
    async updateKeepHumanOwnerFlag(value) {
      this.keepHumanOwnerOnReopen = value;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_keep_human_owner_on_reopen: value,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_KEEP_HUMAN_OWNER_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Resolved human reopen through assistant triage
    async updateAssistantTriageResolvedHumanReopenFlag(value) {
      const previousValue = this.assistantTriagesResolvedHumanReopen;
      this.assistantTriagesResolvedHumanReopen = value;
      this.assistantTriagesResolvedHumanReopenSaving = true;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_assistant_triages_resolved_human_reopen: value,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_REOPEN_TRIAGE_SUCCESS'
          )
        );
      } catch (error) {
        this.assistantTriagesResolvedHumanReopen = previousValue;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.assistantTriagesResolvedHumanReopenSaving = false;
      }
    },
    // remote-assignment-enforcement.UI.1 — this inbox delegates protected assignments to the configured service.
    async updateManualAssignmentRemoteEnabled(value) {
      const previousValue = this.manualAssignmentRemoteSavedValue;
      this.manualAssignmentRemoteEnabled = value;
      this.manualAssignmentRemoteSaving = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_manual_assignment_remote_enabled: value,
          },
        });
        this.manualAssignmentRemoteSavedValue = value;
        useAlert(
          this.$t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_MANUAL_ASSIGNMENT_REMOTE_SUCCESS'
          )
        );
      } catch (error) {
        this.manualAssignmentRemoteEnabled = previousValue;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.manualAssignmentRemoteSaving = false;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Assistant bypass labels (auto-saves on change)
    async updateBypassLabels(labels) {
      this.assistantBypassLabels = labels;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          auto_assignment_config: {
            ...(this.inbox.auto_assignment_config || {}),
            kaption_assistant_bypass_labels: this.assistantBypassLabels,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_BYPASS_LABELS_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    maxAssignmentLimit: {
      minValue: minValue(1),
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
    >
      <multiselect
        v-model="selectedAgents"
        :options="agentList"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        placeholder="Pick some"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        @select="v$.selectedAgents.$touch"
      />

      <NextButton
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :is-loading="isAgentListUpdating"
        @click="updateAgents"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT_SUB_TEXT')"
    >
      <label class="w-3/4 settings-item">
        <div class="flex items-center gap-2">
          <input
            id="enableAutoAssignment"
            v-model="enableAutoAssignment"
            type="checkbox"
            @change="handleEnableAutoAssignment"
          />
          <label for="enableAutoAssignment">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') }}
        </p>
      </label>

      <div v-if="enableAutoAssignment && isEnterprise" class="py-3">
        <woot-input
          v-model="maxAssignmentLimit"
          type="number"
          :class="{ error: v$.maxAssignmentLimit.$error }"
          :error="maxAssignmentLimitErrors"
          :label="$t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')"
          @blur="v$.maxAssignmentLimit.$touch"
        />

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT') }}
        </p>

        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :disabled="v$.maxAssignmentLimit.$invalid"
          @click="updateInbox"
        />
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - Auto-assign on human reply setting -->
      <label class="w-3/4 settings-item mt-4">
        <div class="flex items-center gap-2">
          <input
            id="kaptionAutoAssignOnHumanReply"
            v-model="kaptionAutoAssignOnHumanReply"
            type="checkbox"
            @change="handleKaptionAutoAssignChange"
          />
          <label for="kaptionAutoAssignOnHumanReply">
            {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_ASSIGN_ON_REPLY') }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{
            $t(
              'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_ASSIGN_ON_REPLY_SUB_TEXT'
            )
          }}
        </p>
      </label>
    </SettingsSection>

    <!-- remote-assignment-enforcement.UI.3 — no local capacity or availability policy is configured here. -->
    <SettingsSection
      :title="
        $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_MANUAL_ASSIGNMENT_REMOTE_TITLE')
      "
      :sub-title="
        $t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_MANUAL_ASSIGNMENT_REMOTE_SUB_TEXT'
        )
      "
    >
      <div class="settings-item">
        <div class="flex items-center gap-2">
          <input
            id="manualAssignmentRemoteEnabled"
            type="checkbox"
            :checked="manualAssignmentRemoteEnabled"
            :disabled="manualAssignmentRemoteSaving"
            @change="updateManualAssignmentRemoteEnabled($event.target.checked)"
          />
          <label for="manualAssignmentRemoteEnabled" class="cursor-pointer">
            {{
              $t(
                'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_MANUAL_ASSIGNMENT_REMOTE_LABEL'
              )
            }}
          </label>
        </div>
        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{
            $t(
              'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_MANUAL_ASSIGNMENT_REMOTE_DESCRIPTION'
            )
          }}
        </p>
      </div>
    </SettingsSection>

    <!-- NOTICE: KAPTION-INTEGRATION - Auto-unassign on resolve settings -->
    <SettingsSection
      :title="$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_SUB_TEXT')
      "
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_LABEL') }}
        </label>
        <div class="flex flex-wrap gap-3">
          <label
            v-for="option in [
              {
                value: 'disabled',
                label: $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_DISABLED'
                ),
              },
              {
                value: 'immediately',
                label: $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_IMMEDIATELY'
                ),
              },
              {
                value: 'end_of_day',
                label: $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_END_OF_DAY'
                ),
              },
              {
                value: 'end_of_week',
                label: $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_AUTO_UNASSIGN_END_OF_WEEK'
                ),
              },
            ]"
            :key="option.value"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="radio"
              name="autoUnassignOnResolve"
              :value="option.value"
              :checked="autoUnassignOnResolve === option.value"
              @change="updateAutoUnassign(option.value)"
            />
            <span class="text-sm text-n-slate-11">{{ option.label }}</span>
          </label>
        </div>
      </div>
    </SettingsSection>

    <!-- NOTICE: KAPTION-INTEGRATION - Post-resolution cooldown settings -->
    <SettingsSection
      v-if="showCooldownSettings"
      :title="$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_TITLE')"
      :sub-title="$t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_SUB_TEXT')"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_LABEL') }}
        </label>
        <div class="flex flex-wrap gap-3">
          <label
            v-for="option in [
              {
                value: 0,
                label: $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_DISABLED'
                ),
              },
              {
                value: 3,
                label: $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_3H'),
              },
              {
                value: 6,
                label: $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_6H'),
              },
              {
                value: 12,
                label: $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_12H'),
              },
            ]"
            :key="option.value"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="radio"
              name="cooldownHours"
              :value="option.value"
              :checked="cooldownHours === option.value"
              @change="updateCooldown(option.value)"
            />
            <span class="text-sm text-n-slate-11">{{ option.label }}</span>
          </label>
        </div>
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - End cooldown at OOH boundary (opt-in) -->
      <label
        v-if="cooldownHours > 0"
        class="w-3/4 settings-item mt-4 cursor-pointer"
      >
        <div class="flex items-center gap-2">
          <input
            id="cooldownEndsAtOohBoundary"
            type="checkbox"
            :checked="cooldownEndsAtOohBoundary"
            @change="updateCooldownBoundaryFlag($event.target.checked)"
          />
          <label for="cooldownEndsAtOohBoundary">
            {{
              $t(
                'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_ENDS_AT_OOH_LABEL'
              )
            }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{
            $t(
              'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_COOLDOWN_ENDS_AT_OOH_SUB_TEXT'
            )
          }}
        </p>
      </label>

      <!-- NOTICE: KAPTION-INTEGRATION - Keep the human owner when a conversation reopens -->
      <div class="w-3/4 settings-item mt-4">
        <div class="flex items-center gap-2">
          <input
            id="keepHumanOwnerOnReopen"
            type="checkbox"
            :checked="keepHumanOwnerOnReopen"
            @change="updateKeepHumanOwnerFlag($event.target.checked)"
          />
          <label for="keepHumanOwnerOnReopen">
            {{
              $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_KEEP_HUMAN_OWNER_LABEL')
            }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{
            $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_KEEP_HUMAN_OWNER_SUB_TEXT')
          }}
        </p>
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - ai-takeover.CUSTOMER_MESSAGE.1 -->
      <div
        v-if="showTakeoverSettings"
        class="w-3/4 settings-item mt-4"
        data-testid="kaption-takeover-customer-message"
      >
        <div class="flex items-center gap-2">
          <input
            id="takeoverCustomerMessageEnabled"
            v-model="takeoverCustomerMessageEnabled"
            type="checkbox"
          />
          <label for="takeoverCustomerMessageEnabled">
            {{ $t('KAPTION_PLATFORM.AI_TAKEOVER.CUSTOMER_MESSAGE.TITLE') }}
          </label>
        </div>
        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{ $t('KAPTION_PLATFORM.AI_TAKEOVER.CUSTOMER_MESSAGE.DESCRIPTION') }}
        </p>
        <template v-if="takeoverCustomerMessageEnabled">
          <textarea
            v-model="takeoverCustomerMessage"
            rows="2"
            class="w-full mt-1"
            :placeholder="
              $t('KAPTION_PLATFORM.AI_TAKEOVER.CUSTOMER_MESSAGE.PLACEHOLDER')
            "
          />
          <p class="pb-1 text-xs not-italic text-n-slate-11">
            {{ $t('KAPTION_PLATFORM.AI_TAKEOVER.CUSTOMER_MESSAGE.HELP') }}
          </p>
        </template>
        <NextButton
          class="mt-2"
          sm
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="takeoverCustomerMessageSaving"
          @click="saveTakeoverCustomerMessage"
        />
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - Opt-in assistant triage for resolved human reopens -->
      <div class="w-3/4 settings-item mt-4">
        <div class="flex items-center gap-2">
          <input
            id="assistantTriagesResolvedHumanReopen"
            type="checkbox"
            :checked="assistantTriagesResolvedHumanReopen"
            :disabled="assistantTriagesResolvedHumanReopenSaving"
            @change="
              updateAssistantTriageResolvedHumanReopenFlag(
                $event.target.checked
              )
            "
          />
          <label
            for="assistantTriagesResolvedHumanReopen"
            class="cursor-pointer"
          >
            {{
              $t(
                'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_REOPEN_TRIAGE_LABEL'
              )
            }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-n-slate-11">
          {{
            $t(
              'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_REOPEN_TRIAGE_SUB_TEXT'
            )
          }}
        </p>
      </div>
    </SettingsSection>

    <!-- NOTICE: KAPTION-INTEGRATION - Assistant bypass labels -->
    <SettingsSection
      :title="
        $t('INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_BYPASS_LABELS_TITLE')
      "
      :sub-title="
        $t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_BYPASS_LABELS_SUB_TEXT'
        )
      "
    >
      <!-- NOTICE: KAPTION-INTEGRATION - v-model + explicit @update (auto-saves on
           change), matching the reports-dashboard usage of KaptionFilterSelect. -->
      <KaptionFilterSelect
        v-model="assistantBypassLabels"
        multiple
        :options="accountLabelOptions"
        icon="i-lucide-tag"
        :placeholder="
          $t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_BYPASS_LABELS_PLACEHOLDER'
          )
        "
        :search-placeholder="
          $t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.KAPTION_ASSISTANT_BYPASS_LABELS_PLACEHOLDER'
          )
        "
        @update:model-value="updateBypassLabels"
      />
    </SettingsSection>
  </div>
</template>
